// frontend/src/api/unbounceClient.js
// Created: April 8, 2025
// This file contains the Unbounce API client configuration

import axios from 'axios';
import { API_URL } from '../config';
import { getToken } from '../auth/auth';
import { refreshUnbounceToken } from './unbounceApi';

// Create axios instance for Unbounce API
const unbounceClient = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Track token refresh state
let isRefreshingToken = false;
let refreshTokenPromise = null;

// Add request interceptor
unbounceClient.interceptors.request.use(
  async (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor
unbounceClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // If we're not already refreshing the token
      if (!isRefreshingToken) {
        isRefreshingToken = true;
        refreshTokenPromise = refreshUnbounceToken()
          .then((response) => {
            isRefreshingToken = false;
            return response;
          })
          .catch((err) => {
            isRefreshingToken = false;
            throw err;
          });
      }

      try {
        // Wait for token refresh to complete
        await refreshTokenPromise;
        // Retry the original request
        return unbounceClient(originalRequest);
      } catch (refreshError) {
        // If token refresh fails, mark for reconnection
        error.needsReconnect = true;
        return Promise.reject(error);
      }
    }

    // Handle rate limiting
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'];
      const delay = retryAfter ? parseInt(retryAfter) * 1000 : 5000;
      
      await new Promise(resolve => setTimeout(resolve, delay));
      return unbounceClient(originalRequest);
    }

    // Handle network errors
    if (!error.response) {
      error.needsReconnect = true;
    }

    return Promise.reject(error);
  }
);

export default unbounceClient; 